import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../services/authService';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const isActivation = searchParams.get('isActivation') === 'true';

  useEffect(() => {
    if (!token || !email) {
      setError('Geçersiz veya eksik link.');
    }
  }, [token, email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Şifreler eşleşmiyor.');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, newPassword);
      setSuccess(true);
    } catch (err) {
      setError(
        isActivation
          ? 'Geçersiz veya süresi dolmuş link. Lütfen portal giriş sayfasındaki "İlk kez giriş yapıyorum" seçeneğini kullanın.'
          : 'Geçersiz veya süresi dolmuş link. Lütfen tekrar şifre sıfırlama talebinde bulunun.'
      );
    } finally {
      setLoading(false);
    }
  };

  // ─── Başarı ekranı ────────────────────────────────────────────────────────
  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>✓</div>
          <h2 style={styles.title}>
            {isActivation ? 'Şifreniz Belirlendi' : 'Şifre Güncellendi'}
          </h2>
          <p style={styles.successMessage}>
            {isActivation
              ? 'Şifreniz başarıyla belirlendi. Artık sisteme giriş yapabilirsiniz.'
              : 'Şifreniz başarıyla güncellendi.'}
          </p>
          <button style={styles.button} onClick={() => navigate('/login')}>
            Giriş Yap
          </button>
        </div>
      </div>
    );
  }

  // ─── Şifre belirleme formu ────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>
          {isActivation ? 'Şifrenizi Belirleyin' : 'Yeni Şifre Belirle'}
        </h2>
        {isActivation && (
          <p style={styles.description}>
            Hoş geldiniz! Hesabınıza erişmek için lütfen bir şifre belirleyin.
          </p>
        )}
        <form onSubmit={handleSubmit}>
          <div style={styles.field}>
            <label style={styles.label}>
              {isActivation ? 'Şifre' : 'Yeni Şifre'}
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={styles.input}
              required
              minLength={6}
              autoFocus
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Şifre Tekrar</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={styles.input}
              required
            />
          </div>
          <p style={styles.passwordHint}>En az 6 karakter</p>
          {error && <p style={styles.error}>{error}</p>}
          <button
            type="submit"
            style={styles.button}
            disabled={loading || !token}
          >
            {loading
              ? 'Kaydediliyor...'
              : isActivation
              ? 'Şifremi Belirle'
              : 'Şifremi Güncelle'}
          </button>
        </form>
        <p style={styles.backLink}>
          <span style={styles.link} onClick={() => navigate('/login')}>
            ← Giriş sayfasına dön
          </span>
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f0f2f5',
  },
  card: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    width: '360px',
  },
  successIcon: {
    textAlign: 'center',
    fontSize: '48px',
    color: '#38a169',
    marginBottom: '12px',
  },
  title: {
    textAlign: 'center',
    marginBottom: '8px',
    color: '#1a1a2e',
    fontSize: '20px',
  },
  description: {
    textAlign: 'center',
    fontSize: '13px',
    color: '#555',
    marginBottom: '20px',
    lineHeight: '1.6',
  },
  successMessage: {
    textAlign: 'center',
    color: '#555',
    fontSize: '14px',
    marginBottom: '24px',
    lineHeight: '1.6',
  },
  field: {
    marginBottom: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '13px',
    color: '#333',
    fontWeight: '500',
  },
  input: {
    padding: '10px',
    borderRadius: '4px',
    border: '1px solid #d9d9d9',
    fontSize: '14px',
    outline: 'none',
  },
  passwordHint: {
    fontSize: '11px',
    color: '#aaa',
    margin: '-8px 0 12px',
  },
  error: {
    color: '#e53e3e',
    fontSize: '13px',
    marginBottom: '8px',
    lineHeight: '1.5',
  },
  button: {
    width: '100%',
    padding: '11px',
    backgroundColor: '#1a1a2e',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '15px',
    cursor: 'pointer',
    marginTop: '4px',
  },
  backLink: {
    textAlign: 'center',
    marginTop: '16px',
    fontSize: '13px',
  },
  link: {
    color: '#1a1a2e',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
};

export default ResetPasswordPage;